import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { FEATURES, useAccess } from "../hooks/useAccess";
import noImage from "../assets/images/noImage.png";
import "../assets/css/familyClans.css";

const options = [
  { value: "week", label: "This week" },
  { value: "lastWeek", label: "Last week" },
  { value: "month", label: "This month" },
  { value: "lastMonth", label: "Last month" },
  { value: "all", label: "All time" },
  { value: "custom", label: "Custom range" },
];
const numberFormat = new Intl.NumberFormat();
const dateFormat = new Intl.DateTimeFormat(undefined, {
  year: "numeric", month: "short", day: "numeric", timeZone: "Asia/Singapore",
});
const rangeLabel = (period) => {
  if (!period) return "";
  if (!period.startDate && !period.endDate) return "all time";
  const start = dateFormat.format(new Date(period.startDate));
  const end = dateFormat.format(new Date(new Date(period.endDate).getTime() - 1));
  return `${start} – ${end}`;
};

const SvipUsers = () => {
  const { canView } = useAccess();
  const allowed = canView(FEATURES.USERS);
  const [period, setPeriod] = useState("week");
  const [appliedPeriod, setAppliedPeriod] = useState({ period: "week" });
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [periodError, setPeriodError] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [result, setResult] = useState({ data: [], pagination: { total: 0 }, period: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    axios.get("svip/admin/users", {
      params: { ...appliedPeriod, search: appliedSearch, page, limit },
    }).then((response) => {
      if (!active) return;
      if (!response.data.status) throw new Error(response.data.message || "Unable to load SVIP users");
      setResult(response.data);
    }).catch((requestError) => {
      if (active) setError(requestError.response?.data?.message || requestError.message || "Unable to load SVIP users");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [allowed, appliedPeriod, appliedSearch, page, limit]);

  const changePeriod = (event) => {
    const next = event.target.value;
    setPeriod(next);
    setPeriodError("");
    if (next !== "custom") {
      setPage(1);
      setAppliedPeriod({ period: next });
    }
  };
  const applyCustom = (event) => {
    event.preventDefault();
    if (!customStartDate || !customEndDate || customStartDate > customEndDate) {
      setPeriodError("Choose a valid start date and end date.");
      return;
    }
    setPeriodError("");
    setPage(1);
    setAppliedPeriod({ startDate: customStartDate, endDate: customEndDate });
  };
  const total = result.pagination?.total || 0;
  const first = total ? (page - 1) * limit + 1 : 0;
  const last = Math.min(page * limit, total);

  if (!allowed) return <p className="text-danger">You do not have access to Users.</p>;
  return (
    <section className="family-clans-page" aria-labelledby="svip-users-title">
      <div className="page-title">
        <div className="row align-items-end">
          <div className="col-12 col-md-7"><h3 id="svip-users-title" className="mb-2 text-white">SVIP Users</h3></div>
          <div className="col-12 col-md-5"><nav aria-label="breadcrumb" className="breadcrumb-header float-md-end"><ol className="breadcrumb mb-0"><li className="breadcrumb-item"><Link to="/admin/dashboard" className="text-danger">Dashboard</Link></li><li className="breadcrumb-item active" aria-current="page">SVIP Users</li></ol></nav></div>
        </div>
      </div>
      <div className="card family-clans-card">
        <div className="card-header family-clans-toolbar">
          <div><span className="family-clans-count">{numberFormat.format(total)}</span><span className="family-clans-count-label"> qualifying users</span></div>
          <div className="family-clans-toolbar-controls">
            <label className="family-clans-search"><span className="sr-only">Search SVIP users</span><i className="fas fa-search" aria-hidden="true"></i><input type="search" value={search} maxLength={100} placeholder="Search name, username, or user ID" onChange={(event) => { setSearch(event.target.value); setPage(1); }} /></label>
            <label className="family-clans-period"><span>Recharge Period</span><select className="form-control" value={period} onChange={changePeriod}>{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          </div>
        </div>
        {period === "custom" && <form className="family-clan-roster-filters" onSubmit={applyCustom}>
          <label><span>Start date</span><input type="date" className="form-control" value={customStartDate} max={customEndDate || undefined} onChange={(event) => setCustomStartDate(event.target.value)} /></label>
          <label><span>End date</span><input type="date" className="form-control" value={customEndDate} min={customStartDate || undefined} onChange={(event) => setCustomEndDate(event.target.value)} /></label>
          <div className="family-clan-roster-filter-actions"><button type="submit" className="btn btn-sm btn-primary">Show recharge</button></div>
        </form>}
        {(periodError || result.period) && <div className="family-clans-period-summary">{periodError ? <span className="text-danger">{periodError}</span> : <span>Diamond recharge for {rangeLabel(result.period)}</span>}</div>}
        <div className="card-body card-overflow p-0"><div className="table-responsive"><table className="table family-clans-table mb-0">
          <thead><tr><th>User</th><th>User ID</th><th>Diamond Recharge</th><th>SVIP</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan="4" className="family-clans-state">Loading SVIP users…</td></tr>}
            {!loading && Boolean(error) && <tr><td colSpan="4" className="family-clans-state text-danger">{error}</td></tr>}
            {!loading && !error && result.data.map((user) => <tr key={user._id}>
              <td><div className="family-clan-identity"><img src={user.image || noImage} alt="" onError={(event) => { event.currentTarget.src = noImage; }} /><div><strong>{user.name || "Unnamed user"}</strong><div>{user.username ? `@${user.username}` : ""}</div></div></div></td>
              <td><code>{user.uniqueId || "—"}</code></td>
              <td>{numberFormat.format(user.recharge)}</td>
              <td>SVIP {user.svip}</td>
            </tr>)}
            {!loading && !error && !result.data.length && <tr><td colSpan="4" className="family-clans-state">No qualifying SVIP users found.</td></tr>}
          </tbody>
        </table></div></div>
        {!loading && !error && total > 0 && <div className="card-footer family-clans-pagination"><span>Showing {first}–{last} of {total}</span><div className="family-clans-page-controls"><label>Rows <select className="form-select form-select-sm" value={limit} onChange={(event) => { setPage(1); setLimit(Number(event.target.value)); }}><option value={10}>10</option><option value={20}>20</option><option value={50}>50</option><option value={100}>100</option></select></label><button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((current) => current - 1)} aria-label="Previous page">&lsaquo;</button><button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= result.pagination.pages} onClick={() => setPage((current) => current + 1)} aria-label="Next page">&rsaquo;</button></div></div>}
      </div>
    </section>
  );
};

export default SvipUsers;
